import { useState, useEffect } from 'react';
import OSS from 'ali-oss';
import { message } from 'antd';
import {
  FileType,
  OssCallbackConfig,
  OssCallbackResponse,
  OssConfigData,
  OssMediaType,
  UploadFileMark,
} from '@/types/config';
import { getOssConfig } from '@/services/config';
import { v4 as uuidv4 } from 'uuid';

type UploadOptions = {
  onProgress?: (p: number) => void;
  timeout?: number;
  chunkSize?: number; // 分片大小，单位字节
  parallel?: number; // 并发上传分片数
};

type OssUploadOptions = {
  timeout: number;
  headers: Record<string, string>;
  progress?: (p: number) => void;
  callback?: {
    url: string;
    body: string;
    contentType: string;
  };
  parallel?: number;
  partSize?: number;
  mime?: string;
};

type OssUploadError = {
  status?: number;
  code?: string;
  message?: string;
  name?: string;
};

type OssUploadResult = {
  data?: OssCallbackResponse;
};

/**
 * 解析 OSS callback_base64 为 ali-oss 可用的 callback 配置
 */
function parseOssCallback(callbackBase64?: string): OssUploadOptions['callback'] | undefined {
  if (!callbackBase64) return undefined;

  try {
    const decoded = atob(callbackBase64);
    const callbackConfig = JSON.parse(decoded) as OssCallbackConfig;
    if (!callbackConfig.callbackUrl || !callbackConfig.callbackBody) {
      return undefined;
    }

    return {
      url: callbackConfig.callbackUrl,
      body: callbackConfig.callbackBody,
      contentType: callbackConfig.callbackBodyType || 'application/x-www-form-urlencoded',
    };
  } catch (error) {
    console.error('解析 OSS callback 配置失败:', error);
    return undefined;
  }
}

/**
 * 判断是否为上传成功但回调失败（HTTP 203 / CallbackFailed）
 */
function isCallbackFailedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const uploadError = error as OssUploadError;
  if (uploadError.status === 203) return true;

  const errorText = `${uploadError.code || ''} ${uploadError.message || ''} ${uploadError.name || ''}`;
  return /CallbackFailed/i.test(errorText);
}

/**
 * 判断是否为可降级到简单上传的 etag 类错误
 */
function isEtagUploadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const messageText = (error as OssUploadError).message || '';
  return messageText.includes('etag') || messageText.includes('Failed to upload');
}

/**
 * 生成上传文件名：公共转码链路优先使用 file_id
 */
function buildUploadFileName(file: File, config: OssConfigData): string {
  const fileExt = file.name.split('.').pop() || '';
  if (config.callback_base64 && config.file_id) {
    return `${config.file_id}-${uuidv4()}-${Date.now()}.${fileExt}`;
  }
  return `${uuidv4()}-${Date.now()}.${fileExt}`;
}

/**
 * 从上传结果中解析最终落库 URL（优先 callback 返回的 output_url）
 */
function resolveUploadedUrl(
  config: OssConfigData,
  fileName: string,
  result?: OssUploadResult,
): string {
  if (config.callback_base64 && result?.data?.output_url) {
    return result.data.output_url;
  }
  return `${config.url}${fileName}`;
}

/**
 * OSS上传相关的自定义Hook
 * @param autoInit 是否自动初始化
 * @returns OSS配置和操作方法
 */
export const useOssUpload = ({
  autoInit = true,
  fileType = FileType.IMAGE,
  mark = UploadFileMark.DEFAULT,
  mediaType,
}: {
  autoInit?: boolean; // 是否自动初始化
  fileType?: FileType; // 文件类型
  mark?: UploadFileMark; // 上传标记
  mediaType?: OssMediaType; // 课堂视频媒体类型，仅课堂视频/预告片场景需要
}) => {
  const [ossConfig, setOssConfig] = useState<OssConfigData | null>(null);
  const [client, setClient] = useState<OSS | null>(null);
  const [loading, setLoading] = useState(false);

  // 组装 OSS 配置请求参数（仅课堂视频场景透传 media_type）
  const buildOssConfigParams = () => ({
    up_type: fileType,
    mark,
    ...(mediaType !== undefined ? { media_type: mediaType } : {}),
  });

  // 请求OSS配置（默认携带 upload_version=1，由后端决定是否启用公共转码）
  const fetchOssConfig = async (): Promise<OssConfigData | null> => {
    try {
      setLoading(true);
      const config = await getOssConfig(buildOssConfigParams());
      setOssConfig(config);
      return config;
    } catch (error) {
      message.error(typeof error === 'string' ? error : '获取OSS配置失败');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 初始化OSS客户端
  const initOssClient = async (config?: OssConfigData): Promise<OSS | null> => {
    try {
      const ossConf = config || ossConfig || (await fetchOssConfig());
      if (!ossConf) return null;

      const ossClient = new OSS({
        region: 'oss-cn-beijing',
        accessKeyId: ossConf.access_key_id,
        accessKeySecret: ossConf.access_key_secret,
        bucket: ossConf.bucket_name,
        stsToken: ossConf.security_token,
        refreshSTSToken: async () => {
          const info = await getOssConfig(buildOssConfigParams());
          setOssConfig(info);
          return {
            accessKeyId: info.access_key_id,
            accessKeySecret: info.access_key_secret,
            stsToken: info.security_token,
          };
        },
        refreshSTSTokenInterval: 300000, // 5分钟刷新一次token
        timeout: 600000, // 设置超时时间为10分钟，适应大文件上传
      });

      setClient(ossClient);
      return ossClient;
    } catch (error) {
      message.error('初始化OSS客户端失败');
      return null;
    }
  };

  // 上传文件到OSS，根据文件大小自动选择普通上传或分片上传
  const uploadToOss = async (file: File, options?: UploadOptions): Promise<string | null> => {
    // 固定使用同一份配置，避免 state 更新导致 client / path / url 不一致
    let activeConfig = ossConfig;
    let activeClient = client;

    try {
      if (!activeConfig) {
        activeConfig = await fetchOssConfig();
        if (!activeConfig) return null;
        activeClient = null;
      } else if (activeConfig.callback_base64) {
        // 公共转码的 file_id 和 callback 属于单个文件，批量上传时不可复用
        activeConfig = await fetchOssConfig();
        if (!activeConfig) return null;
        activeClient = null;
      }

      if (!activeClient) {
        activeClient = await initOssClient(activeConfig);
        if (!activeClient) return null;
      }

      const {
        onProgress,
        timeout = 600000, // 默认10分钟超时
        chunkSize = 2 * 1024 * 1024, // 默认2MB分片，增大分片以提高效率
        parallel = 5, // 默认5个并发，增加并发数
      } = options || {};

      const fileName = buildUploadFileName(file, activeConfig);
      const objectName = `${activeConfig.path || ''}${fileName}`;
      const callback = parseOssCallback(activeConfig.callback_base64);

      const uploadOptions: OssUploadOptions = {
        timeout,
        headers: {
          'Cache-Control': 'no-cache', // 防止缓存问题
        },
      };

      if (callback) {
        uploadOptions.callback = callback;
      }

      if (onProgress) {
        uploadOptions.progress = (p: number) => {
          onProgress(Math.floor(p * 100));
        };
      }

      let result: OssUploadResult | undefined;

      try {
        // 根据文件大小决定使用哪种上传方式
        if (file.size > 5 * 1024 * 1024) {
          // 大于5MB使用分片上传
          const multipartOptions: OssUploadOptions = {
            ...uploadOptions,
            parallel,
            partSize: chunkSize,
            mime: file.type,
          };

          result = (await activeClient.multipartUpload(
            objectName,
            file,
            multipartOptions,
          )) as OssUploadResult;
        } else {
          // 小文件使用简单上传
          result = (await activeClient.put(objectName, file, uploadOptions)) as OssUploadResult;
        }
      } catch (uploadError: unknown) {
        // 上传成功但 OSS 回调失败：文件已就绪，使用自拼 URL 落库，后端定时任务会兜底转码
        if (activeConfig.callback_base64 && isCallbackFailedError(uploadError)) {
          message.success('上传成功，正在进行后台转码...');
          return `${activeConfig.url}${fileName}`;
        }

        // 如果是etag错误，尝试使用简单上传作为备选方案
        if (isEtagUploadError(uploadError)) {
          message.warning('上传大文件可能需要较长时间，请耐心等待...');
          try {
            result = (await activeClient.put(objectName, file, {
              ...uploadOptions,
              timeout: 1200000, // 增加超时时间到20分钟
            })) as OssUploadResult;
          } catch (fallbackError: unknown) {
            if (activeConfig.callback_base64 && isCallbackFailedError(fallbackError)) {
              message.success('上传成功，正在进行后台转码...');
              return `${activeConfig.url}${fileName}`;
            }
            throw fallbackError;
          }
        } else {
          throw uploadError;
        }
      }

      return resolveUploadedUrl(activeConfig, fileName, result);
    } catch (error) {
      console.error('上传文件失败:', error);
      const errorMsg = error instanceof Error ? error.message : '未知错误';
      message.error(`上传文件失败: ${errorMsg}`);
      return null;
    }
  };

  // 取消上传（用于中断正在进行的上传）
  const cancelUpload = async (uploadId: string, objectName: string): Promise<boolean> => {
    try {
      if (!client) return false;
      await client.abortMultipartUpload(objectName, uploadId);
      return true;
    } catch (error) {
      console.error('取消上传失败:', error);
      return false;
    }
  };

  // 自动初始化
  useEffect(() => {
    if (autoInit) {
      fetchOssConfig().then((config) => {
        if (config) {
          initOssClient(config);
        }
      });
    }
  }, [autoInit]);

  return {
    ossConfig,
    client,
    loading,
    fetchOssConfig,
    initOssClient,
    uploadToOss,
    cancelUpload,
  };
};

export default useOssUpload;
